import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Mountain, Gamepad2, MessageCircle, Info, Check, Star, Zap, Compass, Sword, Shield } from 'lucide-react';
import CenteredLogo from '../components/CenteredLogo';

const Ares = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  const features = [
    'Loot mai bun și mai consistent',
    'Farm mai rapid și eficient',
    'Sisteme de găsire a prietenilor',
    'Casă protejată împotriva griefing-ului',
    'Server optimizat și actualizat constant',
    'Experiență RPG modernă și fluidă',
  ];

  const steps = [
    'Instalează Hytale de pe site-ul oficial',
    'Adaugă serverul Ares.SimplyGods.ro în lista de servere',
    'Conectează-te și începe aventura!',
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Parallax Background */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px]" />
      </motion.div>

      {/* Centered Logo - Desktop only */}
      <CenteredLogo />

      {/* Hero Section */}
      <section className="relative py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6"
            >
              <Mountain className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-blue-400">Hytale RPG Server</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              SimplyGods{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                #Ares
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-[#b3b3c2]"
            >
              Serverul nostru dedicat Hytale, gândit pentru explorare, creativitate 
              și experiențe moderne de multiplayer.
            </motion.p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* How to Play Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="glass rounded-2xl p-6 md:p-8 border border-white/10"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Gamepad2 className="w-6 h-6 text-blue-500" />
                </div>
                <h2 className="text-2xl font-semibold">Cum mă joc</h2>
              </div>

              <p className="text-[#b3b3c2] mb-6">
                Este foarte simplu, doar urmează pașii:
              </p>

              <ol className="space-y-4 mb-8">
                {steps.map((step, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 text-sm flex items-center justify-center font-medium">
                      {index + 1}
                    </span>
                    <span className="text-[#b3b3c2]">{step}</span>
                  </motion.li>
                ))}
              </ol>

              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#b3b3c2]">
                    Serverul va fi disponibil odată cu lansarea oficială a jocului Hytale. 
                    Rămâi conectat pe Discord pentru updates!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Discord Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="glass rounded-2xl p-6 md:p-8 border border-white/10"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#5865F2]/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-[#5865F2]" />
                </div>
                <h2 className="text-2xl font-semibold">Discord #Ares</h2>
              </div>

              <p className="text-[#b3b3c2] mb-6">
                Alătură-te comunității pe Discord #Ares pentru a fi primul care află 
                când serverul devine disponibil.
              </p>

              <div className="p-4 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/30 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#5865F2] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#b3b3c2]">
                    Aici vei găsi toate informațiile legate de serverul Ares, 
                    inclusiv data lansării și noutăți despre dezvoltare.
                  </p>
                </div>
              </div>

              <a
                href="https://discord.gg/RGq8k4fNBk"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full justify-center border-[#5865F2]/50 text-[#5865F2] hover:bg-[#5865F2]/10"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
                <span>discord.gg/RGq8k4fNBk</span>
              </a>
            </motion.div>
          </div>

          {/* Server Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass rounded-2xl p-6 md:p-8 border border-white/10"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold">Detalii despre server</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-[#b3b3c2] leading-relaxed">
                  <span className="text-white font-medium">#Ares</span> este serverul nostru de Hytale 
                  construit din dorința de a ne distra împreună, dar și de a oferi o experiență RPG modernă, 
                  fluidă și plină de progres.
                </p>
                
                <p className="text-[#b3b3c2] leading-relaxed">
                  Am adunat o colecție de moduri atent alese, care îți fac aventura mai rapidă, 
                  mai plină de recompense și mult mai prietenoasă cu jucătorii noi.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-500" />
                  Ce oferim
                </h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-[#b3b3c2]">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-center">
                <Compass className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <h4 className="font-medium text-white">Explorare</h4>
                <p className="text-sm text-[#b3b3c2]">Lumi vaste de descoperit</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 text-center">
                <Sword className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <h4 className="font-medium text-white">Aventură</h4>
                <p className="text-sm text-[#b3b3c2]">Quest-uri și provocări</p>
              </div>
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <h4 className="font-medium text-white">Protecție</h4>
                <p className="text-sm text-[#b3b3c2]">Construiește în siguranță</p>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
              <p className="text-center text-lg">
                <span className="text-white font-medium">Hai să descoperi lumea noastră</span>
                <span className="text-[#b3b3c2]"> – aventura începe aici.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Ares;
